import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Empty,
  Flex,
  Modal,
  Select,
  Space,
  Spin,
  Typography,
  message,
} from "antd";
import { UsergroupAddOutlined } from "@ant-design/icons";
import { NormalModal } from "@/components/modals/NormalModal";
import { axiosInstance } from "@/utilities";
import {
  API_URL_PERMISSIONS_PARTICIPATION,
  API_URL_PIM_PRODUCTS,
} from "@/services/XCONS_ENV";
import { StoreList } from "@/components/drag-list";
import { debounce } from "@/utilities/debounce";
import { AgregarOperador, REMOVER, type Nivel } from "./CatalogAdminsDrawer";
import { CargosDeOperador } from "./CargosDeOperador";
import type {
  ProductParticipant,
  ProductParticipantCandidate,
  ProductParticipants,
} from "./types";
import { NIVEL } from "@/utilities/niveles-de-permiso";

const { Text, Paragraph } = Typography;

/**
 * Los tres niveles del producto (R4), de mayor a menor como en Permisos del Catálogo.
 * **Lector** existe desde xcons-docs#603: a quien está en "Por participación" le hace ver
 * el producto sin editarlo. No hay Por participación: el backend no lo acepta sobre un
 * producto.
 */
const NIVELES: Nivel[] = [
  {
    value: "admin",
    label: NIVEL.admin,
    hint: "Editar, publicar, aprobar y archivar este producto, y sumar participantes",
  },
  {
    value: "editor",
    label: NIVEL.editor,
    hint: "Editar este producto y mandarlo a revisión",
  },
  {
    value: "viewer",
    label: NIVEL.viewer,
  },
];

interface Props {
  /** El `entity_id`, no el SKU: es lo que guarda la participación. */
  productId: number;
  /** Se avisa al padre cuando cambió algo, para que refresque su propio estado. */
  onChanged?: () => void;
}

/**
 * Participantes de un producto: el contenido del panel de Permisos de un producto
 * publicado. El trigger y el panel los pone `PermisosDelProducto`, con el mismo
 * `LateralModal` que Permisos del Catálogo; como ése usa `destroyOnClose`, esto se monta
 * al abrir y `refresh()` en el mount alcanza.
 *
 * **Es una sola lista, no una por nivel de scope.** En presupuestos y pedidos los
 * participantes se agrupan por store y por seller, porque una participación declara *en
 * qué carácter* participa alguien. Un producto es global —no pertenece a ningún store—
 * así que su `participation_type` es siempre `'global'`: hay un solo grupo, y el orden
 * dentro de ese grupo define el dueño.
 *
 * **El dueño es el primero de la lista** y se cambia arrastrando, igual que en el resto
 * del sistema; lo único distinto es cuál es el nivel primario.
 *
 * Agregar y la fila son los de Permisos del Catálogo (xcons-docs#409): el mismo popup, y
 * el nivel a la derecha con Remover como última opción.
 *
 * Los permisos no se calculan acá: el backend devuelve `canManage` ya resuelto —Admin
 * del producto o Admin de catálogo—, así que la regla vive en un solo lugar.
 */
export const ProductParticipantsPanel: React.FC<Props> = ({
  productId,
  onChanged,
}) => {
  const [data, setData] = useState<ProductParticipants | null>(null);
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [candidatos, setCandidatos] = useState<ProductParticipantCandidate[]>(
    [],
  );
  const [buscando, setBuscando] = useState(false);
  const [agregando, setAgregando] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axiosInstance.get<ProductParticipants>(
        `${API_URL_PIM_PRODUCTS}/${productId}/participants`,
      );
      setData(res);
    } catch {
      message.error("No se pudieron cargar los participantes");
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const buscarCandidatos = useMemo(
    () =>
      debounce(async (texto: string) => {
        setBuscando(true);
        try {
          const { data: res } = await axiosInstance.get<
            ProductParticipantCandidate[]
          >(`${API_URL_PIM_PRODUCTS}/${productId}/participant-candidates`, {
            params: { q: texto },
          });
          setCandidatos(res ?? []);
        } catch {
          setCandidatos([]);
        } finally {
          setBuscando(false);
        }
      }, 350),
    [productId],
  );

  const participantes = data?.participants ?? [];
  const canManage = Boolean(data?.canManage);

  /**
   * Guarda nivel y posición vía el endpoint genérico de participaciones.
   *
   * `participationType: 'global'` e `identifier` vacío en todas: es lo que define el
   * nivel del producto. El backend canonicaliza la ficha, así que se puede mandar
   * `contactId` sin pensar desde qué store se está operando.
   */
  const guardar = async (
    cambios: Array<{ contactId: number; value: string; position: number }>,
  ) => {
    if (!cambios.length) {
      message.info("No hay cambios para guardar");
      return false;
    }
    setGuardando(true);
    try {
      // En serie y no en paralelo: reordenar manda varias filas que compiten por
      // `position=0`, y el backend resuelve el conflicto mirando el estado de la tabla.
      for (const cambio of cambios) {
        await axiosInstance.post(API_URL_PERMISSIONS_PARTICIPATION, {
          contactId: cambio.contactId,
          permissionCode: "product",
          entityId: productId,
          participationType: "global",
          participationIdentifier: "",
          value: cambio.value,
          position: cambio.position,
        });
      }
      message.success("Participantes actualizados");
      await refresh();
      onChanged?.();
      return true;
    } catch (e) {
      const res = (e as { response?: { data?: { message?: string } } })
        ?.response;
      message.error(res?.data?.message ?? "No se pudieron guardar los cambios");
      return false;
    } finally {
      setGuardando(false);
    }
  };

  /** El drag devuelve la lista completa: se manda sólo lo que cambió de posición. */
  const reordenar = (lista: any[]) => {
    const cambios = lista
      .filter((item) => {
        const enServidor = participantes.find((p) => p.id === item.raw?.id);
        return (
          !enServidor ||
          enServidor.position !== item.position ||
          enServidor.value !== item.role
        );
      })
      .map((item) => ({
        contactId: item.raw.contactId,
        value: item.role,
        position: item.position,
      }));

    guardar(cambios);
  };

  const quitar = async (id: number) => {
    try {
      await axiosInstance.delete(`${API_URL_PERMISSIONS_PARTICIPATION}/${id}`);
      message.success("Participante eliminado");
      await refresh();
      onChanged?.();
    } catch (e) {
      const res = (e as { response?: { data?: { message?: string } } })
        ?.response;
      message.error(
        res?.data?.message ?? "No se pudo eliminar el participante",
      );
    }
  };

  const confirmarQuitar = (participante: ProductParticipant) =>
    Modal.confirm({
      title: "¿Remover del producto?",
      content: `${participante.name} deja de participar de este producto.`,
      okText: "Remover",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: () => quitar(participante.id),
    });

  /**
   * Suma a alguien al final de la lista.
   *
   * Nace en `MAX+1`, nunca en 0: el dueño no se pisa. Y un producto heredado sin dueño
   * sigue sin dueño hasta que alguien lo arrastre al primer lugar, que es lo que la
   * regla pide.
   */
  const agregar = async (
    candidato: ProductParticipantCandidate,
    level: string,
  ) => {
    const siguiente =
      participantes.reduce((max, p) => Math.max(max, p.position), -1) + 1;

    const ok = await guardar([
      {
        contactId: candidato.contactId,
        value: level,
        position: siguiente || 1,
      },
    ]);
    if (ok) {
      setCandidatos([]);
      setAgregando(false);
    }
  };

  return (
    // El mismo padding que Permisos del Catálogo: `LateralModal` no le pone al body.
    <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
      <Flex vertical gap={16}>
        {/* Sin aviso para quien no administra, Editor o no: el selector deshabilitado ya
            dice que no puede cambiar la lista (xcons-docs#466). */}

        {/* Con dueño, la lista lo marca en su fila. Sin dueño no se dice nada aparte
            (xcons-docs#479): sin participantes ya lo dice el vacío de abajo. */}

        {canManage && (
          <Flex justify="flex-end">
            <Button
              size="small"
              shape="round"
              icon={<UsergroupAddOutlined />}
              onClick={() => setAgregando(true)}
            >
              Agregar
            </Button>
          </Flex>
        )}

        <NormalModal
          footer={null}
          destroyOnClose
          open={agregando}
          onCancel={() => setAgregando(false)}
          title="Agregar operador"
        >
          <AgregarOperador
            niveles={NIVELES}
            nivelInicial="editor"
            elegirCargo={false}
            candidatos={candidatos.filter(
              (c) => !participantes.some((p) => p.contactId === c.contactId),
            )}
            buscando={buscando}
            guardando={guardando}
            onSearch={buscarCandidatos}
            onVolver={() => setAgregando(false)}
            onGuardar={agregar}
          />
        </NormalModal>

        {loading ? (
          <Flex justify="center" style={{ padding: 24 }}>
            <Spin />
          </Flex>
        ) : participantes.length ? (
          <StoreList
            title="Participantes"
            hideSelect
            participants={participantes.map((p) => ({
              id: p.id,
              name: p.name,
              role: p.value,
              position: p.position,
              raw: p,
            }))}
            getParticipants={refresh}
            onChange={canManage ? reordenar : () => undefined}
            renderHeader={(participante: any) => (
              // Una línea, como la fila de Permisos del Catálogo: nombre a la izquierda y
              // nivel a la derecha. Reemplaza el header propio de `StoreList` para sumar el
              // selector, pero el dueño va como ahí y en toda la plataforma: "(Dueño)" en
              // gris, del `isOwner` del backend. Sin `canManage` el selector se ve
              // deshabilitado, no escondido (R11).
              // El selector va en la línea del nombre, no centrado en la fila: con muchos
              // stores quedaba lejos de quién era (xcons-docs#466).
              <Flex vertical style={{ width: "100%", minWidth: 0 }}>
                <Flex align="center" justify="space-between" gap={8}>
                  <Flex align="center" gap={8} wrap="wrap" style={{ minWidth: 0 }}>
                    <Text strong style={{ fontSize: 12 }}>
                      {participante.name}
                    </Text>
                    {participante.raw?.isOwner && (
                      <Text type="secondary">(Dueño)</Text>
                    )}
                  </Flex>
                  <Select<string>
                    size="small"
                    style={{ width: 150, flexShrink: 0 }}
                    value={participante.role}
                    loading={guardando}
                    disabled={!canManage || guardando}
                    onChange={(value) =>
                      value === REMOVER
                        ? confirmarQuitar(participante.raw)
                        : guardar([
                            {
                              contactId: participante.raw.contactId,
                              value,
                              position: participante.position,
                            },
                          ])
                    }
                    options={[
                      ...NIVELES.map((n) => ({
                        value: n.value,
                        label: n.label,
                        title: n.hint,
                      })),
                      // Al final y en rojo, como en Permisos del Catálogo: el rojo lo pone
                      // `config.css`, que matchea el `title`.
                      { value: REMOVER, label: "Remover", title: "Remover" },
                    ]}
                  />
                </Flex>
                {/* Abajo del nombre, de qué store y seller es (xcons-docs#460), como lista
                    y no como tags (#466): son los cargos de la persona, no el que se eligió
                    al sumarla, que no se guarda. */}
                <CargosDeOperador
                  lista
                  permissions={participante.raw?.permissions}
                />
              </Flex>
            )}
          />
        ) : (
          <Empty
            description={
              <Space direction="vertical" size={0}>
                <Text>Este producto no tiene participantes</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Los productos que ya estaban en el catálogo no tienen: los
                  administran los Admin de catálogo.
                </Text>
              </Space>
            }
          />
        )}

        {canManage && participantes.length > 1 && (
          <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
            El primero de la lista es el dueño. Se cambia arrastrando.
          </Paragraph>
        )}
      </Flex>
    </div>
  );
};
